import { Controller } from "@hotwired/stimulus"

const STORAGE_PREFIX = "depozen:turbo_index:row_layout:"

// Restores the user's preferred index row layout from localStorage and keeps it in sync when toggling.
export default class extends Controller {
  static targets = ["link"]
  static values = {
    model: String,
    layout: String,
  }

  connect() {
    this.onFrameLoad = this.onFrameLoad.bind(this)
    this.onScopeBarClick = this.onScopeBarClick.bind(this)
    document.addEventListener("turbo:frame-load", this.onFrameLoad)
    this.element.addEventListener("click", this.onScopeBarClick, true)

    const stored = this.storedLayout()
    const preferred = stored || this.layoutValue

    this.syncNavigationLinks(preferred)

    if (stored && stored !== this.layoutValue) {
      const link = this.linkTargets.find((element) => element.dataset.layout === stored)
      if (link) {
        link.click()
        return
      }
    }

    this.syncActiveState(preferred)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.onFrameLoad)
    this.element.removeEventListener("click", this.onScopeBarClick, true)
  }

  onScopeBarClick(event) {
    const link = event.target.closest("#scope_selector a[href]")
    if (!link) return

    this.pendingScopeOptions = this.optionsFromHref(link.href)
  }

  onFrameLoad(event) {
    const frameId = this.searchResultsFrameId()
    if (!frameId || event.target.id !== frameId) return

    const rendered = this.detectLayoutFromFrame(event.target)
    if (!rendered) return

    const preferred = this.preferredLayout()
    const baseOptions = this.baseOptionsForFrameSync()

    if (baseOptions) {
      this.syncLayoutLinkHrefs(baseOptions)
      this.syncNavigationLinks(preferred)
    }

    if (preferred && preferred !== rendered) {
      const link = this.linkTargets.find((element) => element.dataset.layout === preferred)
      if (link) {
        link.click()
        return
      }
    }

    this.layoutValue = rendered
    this.syncActiveState(rendered)
    this.syncNavigationLinks(rendered)
  }

  select(event) {
    const layout = event.currentTarget.dataset.layout
    if (!layout) return

    this.pendingScopeOptions = null
    localStorage.setItem(this.storageKey(), layout)
    this.layoutValue = layout
    this.syncActiveState(layout)
    this.syncNavigationLinks(layout)
  }

  preferredLayout() {
    return this.storedLayout() || this.layoutValue
  }

  detectLayoutFromFrame(frame) {
    const root = frame.querySelector(".index-search-results")
    if (!root) return null

    if (root.classList.contains("index-search-results--cards")) return "cards"
    if (root.classList.contains("index-search-results--table")) return "table"

    return "grid"
  }

  syncNavigationLinks(layout) {
    if (!layout) return

    this.element.querySelectorAll("#scope_selector a[href]").forEach((link) => {
      this.mergeRowLayoutIntoHref(link, layout)
    })
  }

  baseOptionsForFrameSync() {
    if (this.pendingScopeOptions) {
      const options = this.pendingScopeOptions
      this.pendingScopeOptions = null
      return options
    }

    return this.optionsFromLocation()
  }

  optionsFromLocation() {
    return this.optionsFromHref(window.location.href)
  }

  optionsFromHref(href) {
    try {
      const url = new URL(href, window.location.origin)
      const raw = url.searchParams.get("options")
      if (!raw) return null

      return JSON.parse(raw)
    } catch (_error) {
      return null
    }
  }

  syncLayoutLinkHrefs(baseOptions) {
    if (!baseOptions) return

    this.linkTargets.forEach((link) => {
      const layout = link.dataset.layout
      if (!layout) return

      this.applyOptionsToHref(link, { ...baseOptions, row_layout: layout, page: null })
    })
  }

  mergeRowLayoutIntoHref(anchor, layout) {
    try {
      const url = new URL(anchor.href, window.location.origin)
      const raw = url.searchParams.get("options")
      if (!raw) return

      const options = JSON.parse(raw)
      options.row_layout = layout
      this.applyOptionsToHref(anchor, options)
    } catch (_error) {
      // Ignore malformed scope URLs.
    }
  }

  applyOptionsToHref(anchor, options) {
    const url = new URL(anchor.href, window.location.origin)
    url.searchParams.set("options", JSON.stringify(options))
    anchor.href = `${url.pathname}${url.search}${url.hash}`
  }

  syncActiveState(activeLayout) {
    this.linkTargets.forEach((link) => {
      link.classList.toggle("active", link.dataset.layout === activeLayout)
    })

    this.refreshPillIndicator()
  }

  refreshPillIndicator() {
    const pillTabs = this.element.querySelector('[data-controller~="pill-tabs"]')
    if (!pillTabs) return

    const run = (attemptsLeft = 12) => {
      const controller = this.application.getControllerForElementAndIdentifier(pillTabs, "pill-tabs")
      if (controller?.refreshIndicator) {
        controller.refreshIndicator()
        return
      }

      if (attemptsLeft > 0) {
        requestAnimationFrame(() => run(attemptsLeft - 1))
      }
    }

    run()
  }

  searchResultsFrameId() {
    return this.linkTargets[0]?.dataset?.turboFrame
  }

  storedLayout() {
    return localStorage.getItem(this.storageKey())
  }

  storageKey() {
    return `${STORAGE_PREFIX}${this.modelValue}`
  }
}
